import { Link } from "react-router-dom";
import type { PricingQuoteResponse, ProductCard } from "@itaas/pep-contracts";
import type { PepLocale } from "../../i18n/types";
import { ProductPromotionBadge } from "../../components/commercial/ProductPromotionBadge";
import { PromoPrice } from "../../components/commercial/PromoPrice";
import { editionLabel } from "../../utils/labels";
import { commercialCtaLabelForProduct } from "../../utils/commercial-cta";
import { isPromotionActive } from "../../utils/product-promotion";

interface ProductPricingAsideProps {
  locale: PepLocale;
  product: ProductCard;
  prices: Record<string, PricingQuoteResponse>;
  configureTo: string;
  pricingPath?: string;
  /** Override primary aside CTA label (defaults to configure copy). */
  primaryCtaLabel?: string;
  /** Hide “see pricing” when product has no public price. Default true. */
  showPricingLink?: boolean;
}

export function ProductPricingAside({
  locale,
  product,
  prices,
  configureTo,
  pricingPath = "/pricing",
  primaryCtaLabel,
  showPricingLink = true,
}: ProductPricingAsideProps) {
  const editions = product.editions ?? [];
  const entry = editions[0];
  const entryQuote = entry ? prices[entry] : undefined;
  const promo = isPromotionActive(product.promotion);
  const ctaLabel = primaryCtaLabel ?? commercialCtaLabelForProduct(product, locale);

  return (
    <aside className="pep-pd-price-card" aria-labelledby="pep-pd-price-title">
      <h2 id="pep-pd-price-title" className="pep-pd-price-card__title">
        {locale === "en" ? "Simple, flexible pricing" : "Tarification simple et flexible"}
      </h2>
      {promo ? (
        <div className="mb-2">
          <ProductPromotionBadge promotion={product.promotion} locale={locale} />
        </div>
      ) : null}

      {entryQuote ? (
        <div className="pep-pd-price-card__entry">
          <p className="pep-pd-price-card__from">
            {locale === "en" ? "From" : "À partir de"}
          </p>
          {entryQuote.originalAmountFormatted ? (
            <PromoPrice
              original={entryQuote.originalAmountFormatted}
              current={entryQuote.amountFormatted}
            />
          ) : (
            <p className="pep-pd-price-card__amount">{entryQuote.amountFormatted}</p>
          )}
          <p className="pep-pd-price-card__meta">
            {editionLabel(entry)} · {locale === "en" ? "USD / month" : "USD / mois"}
          </p>
        </div>
      ) : (
        <p className="text-secondary small">
          {locale === "en" ? "Pricing on request." : "Tarification sur demande."}
        </p>
      )}

      <ul className="pep-pd-price-card__list">
        {editions.slice(0, 4).map((ed) => {
          const q = prices[ed];
          return (
            <li key={ed}>
              <span>{editionLabel(ed)}</span>
              <strong>
                {q?.amountFormatted ??
                  (ed.includes("enterprise")
                    ? locale === "en"
                      ? "Quote"
                      : "Sur devis"
                    : "—")}
              </strong>
            </li>
          );
        })}
      </ul>

      <Link className="btn pep-pd-btn-primary w-100" to={configureTo}>
        {ctaLabel}
      </Link>
      {showPricingLink ? (
        <Link className="btn btn-link w-100 mt-1" to={pricingPath}>
          {locale === "en" ? "See all pricing →" : "Voir les tarifs →"}
        </Link>
      ) : null}
    </aside>
  );
}
